import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@/components/base/Alert';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Modal from '@/components/base/Modal';
import PasswordField from '@/pages/auth/components/PasswordField';
import { systemSettings } from '@/mocks/settings';
import {
  DRIVER_DOCUMENT_KEYS,
  type CreateDriverPayload,
  type Driver,
  type DriverDocumentKey,
  type DriverDocuments,
  type DriverProfilePayload,
} from '@/types';
import DocumentField from './DocumentField';
import ImageUploadField from './ImageUploadField';
import { cloneDocuments, SA_PHONE_RE } from './driverForm';

interface DriverFormModalProps {
  open: boolean;
  driver: Driver | null;
  onClose: () => void;
  onSubmit: (
    payload: CreateDriverPayload | DriverProfilePayload,
    id: string | null,
  ) => Promise<void>;
}

const DOCUMENT_LABEL_KEY: Record<DriverDocumentKey, string> = {
  nationalId: 'drivers.form.nationalId',
  license: 'drivers.form.license',
  vehicle: 'drivers.form.vehicle',
};

const DOCUMENT_ICON: Record<DriverDocumentKey, string> = {
  nationalId: 'ri-id-card-line',
  license: 'ri-roadster-line',
  vehicle: 'ri-truck-line',
};

export default function DriverFormModal({ open, driver, onClose, onSubmit }: DriverFormModalProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(driver);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [documents, setDocuments] = useState<DriverDocuments>(() => cloneDocuments());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFullName(driver?.fullName ?? '');
    setPhone(driver?.phone ?? '');
    setUsername(driver?.username ?? '');
    setPassword('');
    setAvatarUrl(driver?.avatarUrl ?? null);
    setDocuments(cloneDocuments(driver?.documents));
    setErrors({});
    setFormError(null);
    setSaving(false);
  }, [open, driver]);

  const setDocument = (key: DriverDocumentKey, value: DriverDocuments[DriverDocumentKey]) => {
    setDocuments((prev) => ({ ...prev, [key]: value }));
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (!fullName.trim()) next.fullName = t('drivers.required.fullName');
    if (!phone.trim()) next.phone = t('drivers.required.phone');
    else if (!SA_PHONE_RE.test(phone.trim())) next.phone = t('drivers.invalid.phone');
    if (!isEdit) {
      if (!username.trim()) next.username = t('drivers.required.username');
      if (!password) next.password = t('drivers.required.password');
    }
    return next;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setFormError(t('drivers.form.errorFix'));
      return;
    }

    setSaving(true);
    setFormError(null);

    const base: DriverProfilePayload = {
      fullName: fullName.trim(),
      phone: phone.trim(),
      avatarUrl,
      documents: cloneDocuments(documents),
    };

    try {
      if (isEdit) {
        await onSubmit(base, driver?.id ?? null);
      } else {
        await onSubmit({ ...base, username: username.trim(), password }, null);
      }
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message === 'username-taken') {
        setErrors((prev) => ({ ...prev, username: t('drivers.usernameTaken') }));
      } else {
        setFormError(t('drivers.form.errorSave'));
      }
    } finally {
      setSaving(false);
    }
  };

  const uploadLabels = {
    upload: t('drivers.upload.upload'),
    replace: t('drivers.upload.replace'),
    remove: t('drivers.upload.remove'),
    noFile: t('drivers.upload.noFile'),
    tooLarge: t('upload.tooLarge'),
    wrongType: t('upload.wrongType'),
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      size="lg"
      fullScreen
      title={isEdit ? t('drivers.form.editTitle') : t('drivers.form.addTitle')}
      footer={
        <>
          <Button variant="outline" size="md" onClick={onClose} disabled={saving}>
            {t('drivers.form.cancel')}
          </Button>
          <Button type="submit" form="driver-form" variant="primary" size="md" loading={saving}>
            {isEdit ? t('drivers.form.save') : t('drivers.form.create')}
          </Button>
        </>
      }
    >
      <form id="driver-form" className="space-y-6" onSubmit={handleSubmit} noValidate>
        {formError ? <Alert variant="error">{formError}</Alert> : null}

        <section className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground-950">{t('drivers.form.section.basic')}</h4>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              id="driver-fullname"
              name="fullName"
              label={t('drivers.form.fullName')}
              requiredMark
              value={fullName}
              error={errors.fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            <Input
              id="driver-phone"
              name="phone"
              label={t('drivers.form.phone')}
              requiredMark
              value={phone}
              error={errors.phone}
              ltr
              inputMode="tel"
              placeholder="05XXXXXXXX"
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <ImageUploadField
            id="driver-avatar"
            name="avatar"
            label={t('drivers.form.avatar')}
            value={avatarUrl}
            onChange={setAvatarUrl}
            accept={systemSettings.imageUpload.accept}
            maxSizeMB={systemSettings.imageUpload.maxSizeMB}
            hint={t('drivers.form.uploadHint')}
            optionalLabel={t('drivers.form.optional')}
            labels={{
              choose: t('drivers.form.avatarChoose'),
              replace: t('drivers.form.avatarReplace'),
              remove: t('drivers.form.avatarRemove'),
              none: t('drivers.form.avatarNone'),
              tooLarge: t('upload.tooLarge'),
              wrongType: t('upload.wrongType'),
            }}
          />
        </section>

        <section className="space-y-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-foreground-950">{t('drivers.form.section.documents')}</h4>
            <span className="text-xs text-foreground-500">{t('drivers.form.uploadHint')}</span>
          </div>

          {DRIVER_DOCUMENT_KEYS.map((key) => (
            <div key={key} className="flex items-start gap-2.5">
              <span className="mt-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                <i className={`${DOCUMENT_ICON[key]} text-base leading-none`} aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <DocumentField
                  id={`driver-doc-${key}`}
                  name={key}
                  label={t(DOCUMENT_LABEL_KEY[key])}
                  value={documents[key]}
                  onChange={(value) => setDocument(key, value)}
                  accept={systemSettings.upload.accept}
                  maxSizeMB={systemSettings.upload.maxSizeMB}
                  labels={uploadLabels}
                />
              </div>
            </div>
          ))}
        </section>

        {!isEdit ? (
          <section className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground-950">{t('drivers.form.section.login')}</h4>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                id="driver-username"
                name="username"
                label={t('drivers.form.username')}
                requiredMark
                value={username}
                error={errors.username}
                ltr
                autoComplete="off"
                onChange={(e) => setUsername(e.target.value)}
              />
              <PasswordField
                id="driver-password"
                name="password"
                label={t('drivers.form.password')}
                requiredMark
                value={password}
                error={errors.password}
                autoComplete="new-password"
                showLabel={t('drivers.form.showPassword')}
                hideLabel={t('drivers.form.hidePassword')}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="space-y-1.5 rounded-md border border-secondary-200 bg-secondary-100/60 px-3.5 py-3">
              <p className="flex items-start gap-2 text-xs leading-relaxed text-secondary-900">
                <i className="ri-information-line mt-0.5 text-base text-secondary-700" aria-hidden="true" />
                {t('drivers.form.noteLogin')}
              </p>
              <p className="flex items-start gap-2 text-xs leading-relaxed text-secondary-900">
                <i className="ri-shield-check-line mt-0.5 text-base text-secondary-700" aria-hidden="true" />
                {t('drivers.form.noteNoApproval')}
              </p>
            </div>
          </section>
        ) : (
          <p className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100/70 px-3.5 py-3 text-xs leading-relaxed text-foreground-600">
            <i className="ri-lock-2-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
            {t('drivers.form.noteUsernameLocked')}
          </p>
        )}
      </form>
    </Modal>
  );
}